/**
 * The comment composer, with `@` autocomplete and a tag picker.
 *
 * The mention half is deliberately the same shape as Cloud's workspace chat
 * (`website-cloud/web/src/components/Chat.tsx`): the trigger is a bare `@` at a
 * word boundary followed by what has been typed, matching happens entirely on
 * the client against a list it already holds, and accepting replaces the partial
 * token with `@Name `.
 *
 * Keeping the server out of it matters more here than it does there. A Review
 * comment is written by whoever holds a share link, so the body is untrusted
 * prose; the client resolves names to actor keys and sends *keys*, and the
 * server only checks that each key belongs to this project. Nothing ever parses
 * an `@` server-side.
 *
 * One difference from Cloud, forced by the product: a reviewer is not a Mauvely
 * account, so the list is the people on this review — the author plus everyone
 * who has commented — rather than workspace members.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ds } from '../ds';
import { api, ApiError, type ProjectTag, type ReviewerWire } from '../api';
import { mono, RADIUS } from '../theme';

export interface Mentionable {
  key: string;
  name: string;
  colour: string;
}

/** The author plus every reviewer, as one list for `@`. The server includes
 *  the author's entry, so every viewer — not just the author — can resolve and
 *  mention them. */
export function mentionable(reviewers: ReviewerWire[]): Mentionable[] {
  return reviewers.map((r) => ({
    key: r.key,
    name: r.name,
    colour: r.colour
  }));
}

/**
 * Resolve the `@Name`s in a body to actor keys.
 *
 * Longest name first, so "@Priya Raman" is not matched as "@Priya" when both
 * exist. Anything that does not match a known person stays as plain text — a
 * name typed by hand should not silently become nobody.
 */
export function resolveMentions(body: string, people: Mentionable[]): string[] {
  const found: string[] = [];
  const ordered = [...people].sort((a, b) => b.name.length - a.name.length);
  for (const person of ordered) {
    if (body.includes(`@${person.name}`) && !found.includes(person.key)) {
      found.push(person.key);
    }
  }
  return found;
}

export function Composer({
  people,
  tags,
  placeholder,
  submitLabel,
  projectId,
  onCancel,
  onSubmit
}: {
  people: Mentionable[];
  tags: ProjectTag[];
  placeholder: string;
  submitLabel: string;
  /** Enables the paperclip; without it the composer is text-only. */
  projectId?: string;
  onCancel: () => void;
  onSubmit: (
    body: string,
    mentions: string[],
    tagId: string | null,
    attachmentIds: string[]
  ) => Promise<void>;
}) {
  const { Button, Icon } = ds();
  const input = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState('');
  const [tagId, setTagId] = useState<string | null>(tags[0]?.id ?? null);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Array<{ id: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  async function attach(file: File) {
    if (!projectId || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const res = await api.attachments.upload(projectId, file);
      setAttachments((a) => [...a, { id: res.id, name: file.name }]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That image would not upload.');
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    input.current?.focus();
  }, []);

  /** The `@…` being typed at the caret, or null. */
  const query = useMemo(() => {
    const match = /(^|\s)@([^\s@]*)$/.exec(value);
    return match ? match[2]! : null;
  }, [value]);

  const matches = useMemo(() => {
    if (query === null) return [];
    const needle = query.toLowerCase();
    return people
      .filter((p) => !needle || p.name.toLowerCase().startsWith(needle))
      .slice(0, 6);
  }, [query, people]);

  useEffect(() => setHighlight(0), [query]);

  const accept = (person: Mentionable) => {
    setValue((v) => v.replace(/(^|\s)@([^\s@]*)$/, (_, lead: string) => `${lead}@${person.name} `));
    input.current?.focus();
  };

  const open = query !== null && matches.length > 0;

  async function send() {
    const body = value.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(body, resolveMentions(body, people), tagId, attachments.map((a) => a.id));
      setValue('');
      setAttachments([]);
    } catch (e) {
      // A refused comment must say so. This used to swallow the rejection,
      // which read as "posted" while nothing appeared anywhere.
      setError(e instanceof ApiError ? e.message : 'That did not send. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--surface-raised, var(--surface-card))',
            border: '1px solid var(--border-subtle)',
            borderRadius: RADIUS.control,
            boxShadow: '0 8px 24px rgba(8,6,16,.4)',
            overflow: 'hidden',
            zIndex: 20
          }}
        >
          {matches.map((p, i) => (
            <button
              key={p.key}
              type="button"
              // `mousedown`, not `click`: the textarea loses focus first
              // otherwise, and the caret position the replacement depends on
              // goes with it.
              onMouseDown={(e) => {
                e.preventDefault();
                accept(p);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '7px 10px',
                border: 0,
                textAlign: 'left',
                background: i === highlight ? 'var(--primary-soft)' : 'transparent',
                color: 'var(--text-strong)',
                fontSize: 13
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: p.colour,
                  flex: 'none'
                }}
              />
              {p.name}
            </button>
          ))}
        </div>
      )}

      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {tags.map((t) => {
            const on = t.id === tagId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTagId(on ? null : t.id)}
                style={{
                  ...mono,
                  fontSize: 9.5,
                  padding: '4px 8px',
                  borderRadius: RADIUS.chip,
                  cursor: 'pointer',
                  border: `1px solid ${on ? t.colour : 'var(--border-subtle)'}`,
                  background: on ? `${t.colour}22` : 'transparent',
                  color: on ? t.colour : 'var(--text-muted)'
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      <textarea
        ref={input}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => (h + 1) % matches.length);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => (h - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              accept(matches[highlight]!);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              // Close the list without losing the draft: a stray `@` should not
              // cost someone a paragraph.
              setValue((v) => `${v} `);
              return;
            }
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void send();
          }
          if (e.key === 'Escape') onCancel();
        }}
        rows={3}
        style={{
          width: '100%',
          resize: 'vertical',
          padding: '9px 11px',
          borderRadius: RADIUS.control,
          border: '1px solid var(--border-strong, var(--border-subtle))',
          background: 'var(--surface-sunken, transparent)',
          color: 'var(--text-strong)',
          font: 'inherit',
          fontSize: 13,
          lineHeight: 1.55
        }}
      />

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {attachments.map((a) => (
            <span
              key={a.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11.5,
                padding: '3px 8px',
                borderRadius: RADIUS.chip,
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-body)'
              }}
            >
              {a.name}
              <button
                type="button"
                aria-label={`Remove ${a.name}`}
                onClick={() => setAttachments((list) => list.filter((x) => x.id !== a.id))}
                style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--text-muted)', padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <p style={{ color: 'var(--danger-500)', fontSize: 12.5, margin: '8px 0 0' }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        {projectId && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attach(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              title="Attach an image"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}
            >
              <Icon name="paperclip" size={14} />
            </button>
          </>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void send()} disabled={busy || !value.trim()}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * A body with its `@Name`s drawn as chips.
 *
 * Tinted more strongly when it is *you* who was tagged — in a rail with thirty
 * comments the question being asked is "is any of this for me", and answering it
 * should not require reading. Same reasoning as Cloud's `MentionChip`.
 */
export function Body({
  text,
  people,
  meKey
}: {
  text: string;
  people: Mentionable[];
  meKey: string;
}) {
  const ordered = useMemo(
    () => [...people].sort((a, b) => b.name.length - a.name.length),
    [people]
  );

  const parts: Array<string | Mentionable> = [];
  let rest = text;
  outer: while (rest.length) {
    for (const person of ordered) {
      const token = `@${person.name}`;
      const at = rest.indexOf(token);
      if (at !== -1) {
        if (at > 0) parts.push(rest.slice(0, at));
        parts.push(person);
        rest = rest.slice(at + token.length);
        continue outer;
      }
    }
    parts.push(rest);
    break;
  }

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          <span key={i}>{p}</span>
        ) : (
          <span
            key={i}
            style={{
              display: 'inline',
              padding: '1px 6px',
              margin: '0 1px',
              borderRadius: 999,
              background: p.key === meKey ? p.colour : `${p.colour}2e`,
              color: p.key === meKey ? '#fff' : p.colour,
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            @{p.name}
          </span>
        )
      )}
    </span>
  );
}
